import React, { useCallback, useEffect, useRef, useState } from "react";
import { flatMap } from "lodash/fp";
import isEqual from "lodash/isEqual";
import FormGroup from "../../FormGroup";
import { CheckboxGroup } from "../../Checkbox";
import SelectControls from "./SelectControls";
import type { TOptionsList } from "../../../types";
import { filterOptions } from "../utils";
import withTheme from "../../../hocs/withTheme";
import type { CheckboxOption } from "../../Checkbox/CheckboxGroup";

/** DO If used in a table header for filtering, idle will show on table header column hover.
 *
 *
 * DONT Idle boxed is only used rarely and is not used in table headers for filtering. */

type HeaderControls = {
    selectControlsLabel?: {
        selectAll?: string;
        reset?: string;
    };
};

type MultiSelectMiniFormProps = {
    opened?: boolean;
    options?: TOptionsList | null;
    value?: string[];
    withSelectTool?: boolean;
    onSelect?: (value: string[]) => void;
    searchHidden?: boolean;
    headerControls?: HeaderControls;
    onReset?: (() => string[]) | null;
    onSearch?: ((value: string) => void) | null;
    children?: React.ReactNode;
    className?: string;
};

const MultiSelectMiniForm = (props: MultiSelectMiniFormProps) => {
    const {
        opened = true,
        options = null,
        value = [],
        withSelectTool = true,
        onSelect = () => {},
        searchHidden = false,
        headerControls = {},
        onReset = null,
        onSearch = null,
        children = null,
    } = props;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [filterInput, setFilterInput] = useState("");
    const [selectedOptions, setSelectedOptions] = useState(value);
    const [filteredOptions, setFilteredOptions] = useState<TOptionsList | null>(options);
    const onFilterOptions = useCallback(
        ({ target: { value: v } }: React.ChangeEvent<HTMLInputElement>) => {
            setFilterInput(v);
            setFilteredOptions(filterOptions(options, v));
            if (typeof onSearch === "function") onSearch(v);
        },
        [options, onSearch]
    );
    const toggleItem = useCallback(
        (item: string[]) => {
            const filteredOptionsFlat = (filteredOptions ?? []).map(
                (option) => typeof option === 'string' ? option : option?.value
            );
            const valuesWithoutCurrent = selectedOptions.filter(
                (selectedItem) => !filteredOptionsFlat.includes(selectedItem)
            );
            const newVal = [...new Set([...item, ...valuesWithoutCurrent])];

            setSelectedOptions(newVal);
            onSelect(newVal);
        },
        [filteredOptions, selectedOptions, onSelect]
    );
    const handleCheckAllChange = useCallback(() => {
            const newOptions = flatMap(
                (item) => {
                    if (typeof item === 'string') {
                        return [item];
                    }

                    if (item.value === undefined || item.value === null) {
                        return [];
                    }

                    return [String(item.value)];
                },
            options ?? []
        );

        setSelectedOptions(newOptions);
        onSelect(newOptions);
    }, [options, onSelect]);
    const handleUnCheckAllChange = useCallback(() => {
        if (typeof onReset === "function") {
            const values = onReset() || [];

            setSelectedOptions(values);
            onSelect(values);
        } else {
            setSelectedOptions([]);
            onSelect([]);
        }
    }, [onReset, onSelect]);

    useEffect(() => {
        if (opened && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [opened]);
    useEffect(() => {
        if (!isEqual(value, selectedOptions)) setSelectedOptions(value);
    }, [value]);
    useEffect(() => {
        setFilteredOptions(filterOptions(options, filterInput));
    }, [options, filterInput]);

    const checkboxOptions = (filteredOptions ?? []).filter(
        (option): option is CheckboxOption => typeof option !== 'string'
    );

    return (
        <FormGroup opened={opened}>
            <div className="select__dropdown" id="multiselectFilter-dropdown">
                <input
                    className="select__search"
                    type="search"
                    aria-label="Type to filter available options"
                    value={filterInput}
                    onChange={onFilterOptions}
                    hidden={searchHidden}
                    ref={inputRef}
                />
                <fieldset className="multiselectFilter-dropdown-options">
                    <legend hidden>Options</legend>
                    <CheckboxGroup
                        className="checkbox-group_select"
                        value={selectedOptions}
                        options={checkboxOptions}
                        onChange={toggleItem}
                    />
                    {children}
                </fieldset>
                {withSelectTool && (
                    <SelectControls
                        check={handleCheckAllChange}
                        uncheck={handleUnCheckAllChange}
                        selectControlsLabel={{
                            selectAll: headerControls.selectControlsLabel?.selectAll ?? 'Select all',
                            reset: headerControls.selectControlsLabel?.reset ?? 'Reset',
                        }}
                    />
                )}
            </div>
        </FormGroup>
    );
};


export default withTheme(MultiSelectMiniForm);
